import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { Calendar, Eye, Filter, ImageIcon, Sun, Zap } from 'lucide-react';
import { RailCard } from '~/components/rail-card';
import { Pill } from '~/components/pill';
import { PolicyBadgeThumb } from './policy-badge-thumb';
import { computePromotionStatus, getValidityRelative } from '~/lib/promotion-status';
import { ACTION_TYPE_META_MAP } from './actions/action-type-meta';
import { MOCK_CATEGORIES, MOCK_CUSTOMER_CATEGORIES } from '~/lib/lookups/mock';
import { cn } from '@/lib/utils';
import type { EditorTab } from './editor-shell';
import type { PromotionAction, TierMode } from '~/lib/schemas/actions';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

/** Resolve selected lookup ids to a comma-joined label string (falls back to `#id`). */
function joinLabels(ids: number[] | undefined, options: { id: number; label: string }[]): string {
	return (ids ?? []).map(id => options.find(o => o.id === id)?.label ?? `#${id}`).join(', ');
}

/** Short, human descriptor of an action for the "Applies" summary (per the rail designs). */
function useActionDescriptor() {
	const { t } = useTranslation();
	const modeLabel = (m: TierMode) => t(m === 'byQuantity' ? 'promotion.rail.applyDescByQuantity' : 'promotion.rail.applyDescByAmount');
	const tierRange = (tiers: { value: number }[]) => {
		const values = tiers.map(tier => tier.value).filter(v => Number.isFinite(v));
		return { from: values.length ? Math.min(...values) : 0, to: values.length ? Math.max(...values) : 0, first: values[0] ?? 0 };
	};
	const isTiered = (mode: TierMode, tiers: unknown[]) => mode !== 'fixed' && tiers.length > 1;

	return (a: PromotionAction): string => {
		switch (a.type) {
			case 'percentage-discount': {
				const r = tierRange(a.tiers);
				return isTiered(a.tierMode, a.tiers)
					? t('promotion.rail.applyDescPercentTiered', { from: r.from, to: r.to, mode: modeLabel(a.tierMode) })
					: t('promotion.rail.applyDescPercentFixed', { value: r.first });
			}
			case 'value-discount': {
				const r = tierRange(a.tiers);
				return isTiered(a.tierMode, a.tiers)
					? t('promotion.rail.applyDescValueTiered', { from: r.from, to: r.to, mode: modeLabel(a.tierMode) })
					: t('promotion.rail.applyDescValueFixed', { value: r.first });
			}
			case 'fixed-price': {
				const r = tierRange(a.tiers);
				return isTiered(a.tierMode, a.tiers)
					? t('promotion.rail.applyDescFixedPriceTiered', { mode: modeLabel(a.tierMode) })
					: t('promotion.rail.applyDescFixedPriceFixed', { value: r.first });
			}
			case 'bogo':
				return t('promotion.rail.applyDescBogo', { x: a.x, y: a.y, z: a.z ?? '—' });
			case 'gift-product':
				return t('promotion.rail.applyDescGift', { selections: a.maxSelections, total: a.productIds.length });
			case 'per-product-discount':
				return t('promotion.rail.applyDescPerProduct', { count: a.rows.length });
			case 'category-discount':
				return t('promotion.rail.applyDescCategory', { count: a.rows.length });
			case 'shipping-discount':
				return t('promotion.rail.applyDescShipping', { count: a.rules.length });
			default:
				return t(ACTION_TYPE_META_MAP[(a as PromotionAction).type].labelKey);
		}
	};
}

export function EditorRightRail({ activeTab }: { activeTab: EditorTab }) {
	const { t } = useTranslation();
	const { control } = useFormContext<PromotionFormValues>();
	const v = useWatch({ control }) as PromotionFormValues;
	const describeAction = useActionDescriptor();

	const { activePills, lifecyclePills, visibilityPills } = computePromotionStatus({
		isActive: v.isActive,
		activeFrom: v.activeFrom,
		activeTo: v.activeTo,
		code: v.code || null,
		actionCount: v.actions?.length ?? 0,
		isVisible: v.isVisible,
		hideFromOffers: v.hideFromOffers
	});
	// Rail order: active → visibility → lifecycle (06-editor-spec.md status rail).
	const pills = [...activePills, ...visibilityPills, ...lifecyclePills];

	const relative = getValidityRelative({ activeFrom: v.activeFrom, activeTo: v.activeTo });
	const dateRange =
		v.activeFrom && v.activeTo
			? t('promotion.rail.validFromTo', {
					from: format(v.activeFrom, 'dd MMM yyyy', { locale: el }),
					to: format(v.activeTo, 'dd MMM yyyy', { locale: el })
				})
			: null;

	const conditionBullets: string[] = [];
	if (v.minAmount != null) conditionBullets.push(t('promotion.rail.summaryCartMin', { amount: v.minAmount }));
	if (v.productIds?.length) conditionBullets.push(t('promotion.rail.summaryProducts', { count: v.productIds.length }));
	if (v.categoryIds?.length)
		conditionBullets.push(t('promotion.rail.summaryCategoriesNamed', { names: joinLabels(v.categoryIds, MOCK_CATEGORIES) }));
	if (v.customerCategoryIds?.length)
		conditionBullets.push(
			t('promotion.rail.summaryCustomersNamed', { names: joinLabels(v.customerCategoryIds, MOCK_CUSTOMER_CATEGORIES) })
		);
	conditionBullets.push(
		v.siteIds?.length
			? t('promotion.rail.summarySpecificSites', { count: v.siteIds.length })
			: t(v.applyToSiteMode === 'default' ? 'promotion.rail.summaryDefaultSite' : 'promotion.rail.summaryAllSites')
	);
	if ((v.actions ?? []).some(a => a.combineWithOthers)) conditionBullets.push(t('promotion.rail.summaryCombines'));

	return (
		<div className="flex flex-col gap-3">
			<RailCard label={t('promotion.rail.status')}>
				<div className="flex flex-wrap gap-1.5">
					{pills.map(p => (
						<Pill key={p.labelKey} variant={p.variant} icon={p.icon}>
							{t(p.labelKey)}
						</Pill>
					))}
				</div>
				{dateRange && (
					<p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-muted">
						<Calendar size={12} /> {dateRange}
						{relative && <span className="text-foreground-subtle">({t(relative.key, { count: relative.count })})</span>}
					</p>
				)}
			</RailCard>

			<RailCard label={t('promotion.rail.summary')}>
				<div className={cn('-mx-3 rounded-md p-3', activeTab === 'conditions' && 'bg-brand-subtle')}>
					<p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
						<Filter size={12} className="text-brand" /> {t('promotion.rail.appliesWhen')}
					</p>
					<ul className="mt-1.5 flex flex-col gap-1">
						{conditionBullets.map(b => (
							<li key={b} className="flex items-start gap-1.5 text-xs text-foreground-muted">
								<span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand" />
								{b}
							</li>
						))}
					</ul>
				</div>
				<div className={cn('-mx-3 mt-1 rounded-md p-3', activeTab === 'actions' && 'bg-brand-subtle')}>
					<p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
						<Zap size={12} className="text-brand" /> {t('promotion.rail.applies')}
					</p>
					<ol className="mt-1.5 flex flex-col gap-1">
						{(v.actions ?? []).map((a, i) => (
							<li key={`${a.type}-${i}`} className="flex items-start gap-1.5 text-xs text-foreground-muted">
								<span className="font-semibold text-brand">{i + 1}.</span>
								{describeAction(a)}
							</li>
						))}
					</ol>
				</div>
			</RailCard>

			<RailCard label={t('promotion.rail.badgePreview')} icon={Eye}>
				<div className="overflow-hidden rounded-md border border-border">
					<div className="relative flex aspect-[4/5] items-center justify-center bg-gradient-to-b from-muted to-elevated">
						<ImageIcon size={28} className="text-foreground-subtle/40" />
						<div className="absolute left-2 top-2">
							<PolicyBadgeThumb gradient="sun" icon={Sun} size="sm" />
						</div>
					</div>
					<div className="p-2">
						<p className="text-xs font-medium text-foreground">Φυσική Γ' Λυκείου</p>
						<p className="text-[10px] text-foreground-subtle">Σχολικά Βοηθήματα</p>
						<p className="mt-1 text-xs">
							<span className="text-foreground-subtle line-through">€18.50</span>{' '}
							<span className="font-semibold text-danger-fg">€16.65</span>
						</p>
					</div>
				</div>
				<p className="mt-2 text-center text-[10px] text-foreground-subtle">{t('promotion.rail.badgePreviewCaption')}</p>
			</RailCard>
		</div>
	);
}
